import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal, Pagination, TextInput } from '@carbon/react';
import type { ColumnDef } from '@tanstack/react-table';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { DataTable } from '../components/DataTable';
import { Reveal } from '../components/Reveal';
import { InlineError, PageHeader } from '../components/states';
import { Money, When } from '../components/format';
import { QueueStatus } from '../components/labels';
import { FilterBar, useUrlFilters, type FilterField } from '../components/Filters';

type Payout = {
  id: string; status: string; requestedAmount: string; requestedAt: string;
  reviewDecisionReason?: string | null; payoutReference?: string | null;
  partner: { code: string; displayName: string } | null;
  bankSnapshot?: { nganHang?: string; soTk?: string; chuTk?: string } | null;
};
type Page = { items: Payout[]; total: number; page: number; pageSize: number };

const FIELDS: FilterField[] = [
  { key: 'status', label: 'Trạng thái', kind: 'select', options: [
    ['', 'Tất cả'], ['PENDING_REVIEW', 'Chờ duyệt'], ['APPROVED', 'Đã duyệt'], ['REJECTED', 'Từ chối'], ['PAID', 'Đã chi'], ['CANCELLED', 'Hủy'],
  ] },
];
const KEYS = FIELDS.map((x) => x.key);
const DEFAULTS = { status: 'PENDING_REVIEW' };

const columns: ColumnDef<Payout, any>[] = [
  { header: 'Đại lý', id: 'p', cell: (c) => c.row.original.partner ? c.row.original.partner.code + ' · ' + c.row.original.partner.displayName : '-' },
  { header: 'Số tiền', accessorKey: 'requestedAmount', cell: (c) => <Money v={c.getValue()} /> },
  { header: 'Ngân hàng', id: 'b', cell: (c) => {
    const b = c.row.original.bankSnapshot;
    return b ? `${b.nganHang ?? ''} ${b.soTk ?? ''}`.trim() || '-' : '-';
  } },
  { header: 'Trạng thái', accessorKey: 'status', cell: (c) => <QueueStatus s={c.getValue()} /> },
  { header: 'Lúc', accessorKey: 'requestedAt', cell: (c) => <When v={c.getValue()} /> },
];

export function PayoutsPage() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const f = useUrlFilters(KEYS, { defaults: DEFAULTS });
  const q = useQuery({
    queryKey: ['agent-payouts', f.query, f.page, f.pageSize],
    queryFn: () => api.get<Page>('/api/admin/agent-payouts', { ...f.query, page: f.page, pageSize: f.pageSize }),
    placeholderData: keepPreviousData,
  });
  const [pick, setPick] = useState<Payout | null>(null);
  const [ref, setRef] = useState('');
  const decide = useMutation({
    mutationFn: (approve: boolean) => api.post('/api/admin/agent-payouts/' + pick!.id + '/decide', { approve }),
    onSuccess: () => { setPick(null); qc.invalidateQueries({ queryKey: ['agent-payouts'] }); },
  });
  const paid = useMutation({
    mutationFn: () => api.post('/api/admin/agent-payouts/' + pick!.id + '/pay', { reference: ref }),
    onSuccess: () => { setPick(null); setRef(''); qc.invalidateQueries({ queryKey: ['agent-payouts'] }); },
  });
  // what the open row lets this admin do: approve/reject needs agents.payout.approve, mark paid agents.payout.pay
  const canDecide = pick?.status === 'PENDING_REVIEW' && can('agents.payout.approve');
  const canPay = pick?.status === 'APPROVED' && can('agents.payout.pay');
  return (
    <div className="page">
      <PageHeader title="Chi hoa hồng" lead="Duyệt yêu cầu rồi chuyển khoản và đánh dấu đã chuyển (một người làm cả hai được). Số dư reserve khi yêu cầu, hoàn khi từ chối." />
      <FilterBar id="po" fields={FIELDS} filters={f} exportPath="/api/admin/agent-payouts" />
      <DataTable columns={columns} data={q.data?.items} loading={q.isLoading || q.isPlaceholderData} error={q.error} onRetry={() => q.refetch()}
        onRowClick={(r) => setPick(r)} rowKey={(r) => r.id} empty={{ title: 'Không có yêu cầu', body: 'Đại lý gửi yêu cầu chi từ portal.' }} />
      {q.data && q.data.total > 0 ? (
        <Pagination page={f.page} pageSize={f.pageSize} pageSizes={[20, 50, 100]} totalItems={q.data.total}
          itemsPerPageText="Mỗi trang" pageRangeText={(_c, t) => `trên ${t} trang`}
          itemRangeText={(min, max, total) => `${min} đến ${max} trong ${total.toLocaleString('vi-VN')} yêu cầu`}
          onChange={({ page: p, pageSize: s }) => f.setPage(p, s)} />
      ) : null}
      <Modal open={!!pick} modalHeading={pick ? 'Chi ' + (pick.partner?.code ?? '') + ' · ' + pick.requestedAmount : ''}
        passiveModal={!canDecide && !canPay}
        primaryButtonText={canPay ? 'Đánh dấu đã chuyển' : 'Duyệt'}
        secondaryButtonText={canDecide ? 'Từ chối' : 'Đóng'}
        primaryButtonDisabled={(canPay && ref.trim().length < 1) || decide.isPending || paid.isPending}
        onRequestClose={() => setPick(null)}
        onRequestSubmit={() => {
          if (canPay) paid.mutate();
          else if (canDecide) decide.mutate(true);
        }}
        onSecondarySubmit={() => { if (canDecide) decide.mutate(false); else setPick(null); }}>
        {pick?.bankSnapshot ? <p>{pick.bankSnapshot.nganHang} · <Reveal masked={pick.bankSnapshot.soTk} source="payout" id={pick.id} /> · {pick.bankSnapshot.chuTk}</p> : null}
        {pick && !canDecide && !canPay && (pick.status === 'PENDING_REVIEW' || pick.status === 'APPROVED')
          ? <p className="muted">Tài khoản của bạn không có quyền {pick.status === 'PENDING_REVIEW' ? 'duyệt' : 'xác nhận đã chuyển'} yêu cầu này.</p>
          : null}
        {canPay
          ? <TextInput id="po-ref" labelText="Mã tham chiếu chuyển khoản" value={ref} onChange={(e) => setRef(e.target.value)} />
          : null}
        <InlineError error={decide.error || paid.error} />
      </Modal>
    </div>
  );
}
